// frontend/src/components/LOS/Niyogin/NiyoginAAFlow.tsx
//
// Niyogin Account Aggregator (AA) — Combined Consent + Pipeline Component
// =======================================================================
//
// Single component that handles the full AA + BSA flow:
//
// Phase 0 — Statement Type Selection
//   User selects SALARY or SME before anything else.
//   This drives which report API is called in Step 7.
//
// Phase 1 — Consent Initiation
//   1. User clicks "Start Bank Account Aggregation"
//   2. POST /api/niyogin/aa/consent/initiate/ (with statementType + statementMode)
//      → opens AA URL in new tab
//   3. Background polling starts (GET /consent/status/:handleId/:custId every 5s)
//   4. When consent is ACCEPTED → auto-transitions to Phase 2
//      (user can also click "I've Approved" to manually trigger)
//
// Phase 2 — Automated Pipeline
//   Step 2 — Poll consent/status until ACCEPTED → consentId
//   Step 3 — GET consent/details/:consentId → date range
//   Step 4 — POST fi/request → sessionId
//   Step 5 — Poll data/status until READY → linkRefNumbers[]
//   Step 6 — Poll bsa/session/status/:sessionId/:linkRef for ALL refs (parallel)
//   Step 7 — POST bsa/json-report (backend auto-routes to BSA or SME report) → onDone()

import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  isBuilderMode?: boolean;
}

const POLL_INTERVAL_MS     = 5000;   // all polling uses 5-second intervals
const MAX_CONSENT_POLLS    = 36;     // 36 × 5s = 3 min
const MAX_DATA_POLLS       = 36;
const MAX_BSA_POLLS        = 36;
const BG_POLL_INTERVAL_MS  = 5000;   // background consent poll — same 5s
const MAX_BG_CONSENT_POLLS = 36;     // 36 × 5s = 3 min
const STEP_DELAY_MS        = 2000;   // 2-second pause between each pipeline step

const PIPELINE_STEPS = [
  "Verifying consent approval…",
  "Fetching consent details…",
  "Requesting financial data…",
  "Waiting for data to be ready…",
  "Analysing bank statements…",
  "Fetching BSA report…",
  "Complete ✅",
];

type StatementType = "SALARY" | "SME";
type StepStatus = "pending" | "active" | "done" | "error";
interface StepState { label: string; status: StepStatus; }
type Phase = "selecting" | "idle" | "initiating" | "awaiting_user" | "pipeline" | "done" | "error";

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

// pollUntil: sleeps FIRST, then calls fn — prevents immediate hammering of Niyogin
// on the first attempt (Niyogin needs a moment to process after each prior step).
async function pollUntil<T>(
  fn: () => Promise<T | null>,
  maxAttempts: number,
  intervalMs: number,
  cancelRef?: React.MutableRefObject<boolean>
): Promise<T> {
  for (let i = 0; i < maxAttempts; i++) {
    if (cancelRef?.current) throw new Error("Polling cancelled.");
    await sleep(intervalMs);          // wait BEFORE each attempt
    if (cancelRef?.current) throw new Error("Polling cancelled.");
    const result = await fn();
    if (result !== null) return result;
  }
  throw new Error("Polling timed out — please try again.");
}

const NiyoginAAFlow: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const initiateCalledRef  = useRef(false);
  const pipelineStartedRef = useRef(false);
  const cancelPollRef      = useRef(false);

  const [phase, setPhase] = useState<Phase>("selecting");
  const [aaUrl, setAaUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bgPollCount, setBgPollCount] = useState<number>(0);

  // Statement type chosen by user (SALARY or SME)
  const [statementType, setStatementType] = useState<StatementType>(
    (sessionStorage.getItem("niyogin_aa_statement_type") as StatementType) || "SALARY"
  );

  const [consentHandleId, setConsentHandleId] = useState<string>(
    sessionStorage.getItem("niyogin_aa_consent_handle_id") || ""
  );
  const [customerId, setCustomerId] = useState<string>(
    sessionStorage.getItem("niyogin_aa_customer_id") || ""
  );

  const [steps, setSteps] = useState<StepState[]>(
    PIPELINE_STEPS.map((label, i) => ({ label, status: i === 0 ? "active" : "pending" }))
  );

  const markStep = useCallback((index: number, status: StepStatus) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, status } : s)));
  }, []);

  const activateStep = useCallback((index: number) => {
    setSteps((prev) =>
      prev.map((s, i) => ({
        ...s,
        status: i === index ? "active" : i < index ? "done" : s.status,
      }))
    );
  }, []);

  useEffect(() => {
    // Reset cancel flag on mount (handles React StrictMode double-invoke)
    cancelPollRef.current = false;
    return () => { cancelPollRef.current = true; };
  }, []);

  // ── Phase 2: Full pipeline ────────────────────────────────────────────────────
  const startPipeline = useCallback(
    async (handleId?: string, custId?: string) => {
      if (pipelineStartedRef.current) return;
      pipelineStartedRef.current = true;

      const effectiveHandleId = handleId || consentHandleId;
      const effectiveCustId   = custId   || customerId;

      setPhase("pipeline");
      setSteps(PIPELINE_STEPS.map((label, i) => ({
        label,
        status: i === 0 ? "active" : "pending",
      })));

      try {
        // ── Step 0: Poll consent status until ACCEPTED ──────────────────────────
        activateStep(0);
        const consentId = await pollUntil(async () => {
          const res = await axios.get(
            `${API_BASE_URL}/api/niyogin/aa/consent/status/${effectiveHandleId}/${effectiveCustId}/`
          );
          if (res.data?.success === false) throw new Error(res.data?.error || "Consent status check failed.");
          const { consent_status, consent_id } = res.data || {};
          console.info(`[NiyoginAAFlow] Step 0 poll: consentStatus=${consent_status}`);
          if (consent_status === "ACCEPTED" && consent_id) return consent_id as string;
          return null;
        }, MAX_CONSENT_POLLS, POLL_INTERVAL_MS, cancelPollRef);
        markStep(0, "done");

        // ── Step 1: Fetch consent details ───────────────────────────────────────
        await sleep(STEP_DELAY_MS);
        activateStep(1);
        const detailsRes = await axios.get(
          `${API_BASE_URL}/api/niyogin/aa/consent/details/${consentId}/`
        );
        if (detailsRes.data?.success === false) throw new Error(detailsRes.data?.error || "Failed to fetch consent details.");
        const { date_range_from, date_range_to } = detailsRes.data || {};
        markStep(1, "done");

        // ── Step 2: Submit FI request ───────────────────────────────────────────
        await sleep(STEP_DELAY_MS);
        activateStep(2);
        const fiRes = await axios.post(`${API_BASE_URL}/api/niyogin/aa/fi/request/`, {
          customer_id:       effectiveCustId,
          consent_handle_id: effectiveHandleId,
          consent_id:        consentId,
          date_range_from,
          date_range_to,
        });
        if (fiRes.data?.success === false) throw new Error(fiRes.data?.error || "FI request failed.");
        const sessionId = fiRes.data?.session_id as string;
        markStep(2, "done");

        // ── Step 3: Poll data/status until READY ────────────────────────────────
        // pollUntil already sleeps BEFORE the first attempt (STEP_DELAY_MS via POLL_INTERVAL_MS)
        // so Niyogin has time to process the FI request before we check.
        activateStep(3);
        const linkRefNumbers = await pollUntil(async () => {
          const res = await axios.get(
            `${API_BASE_URL}/api/niyogin/aa/data/status/${consentId}/${sessionId}/${effectiveHandleId}/${encodeURIComponent(effectiveCustId)}/`
          );
          // "No record found" means Niyogin hasn't processed the FI request yet — keep polling
          if (res.data?.success === false) {
            const errMsg = (res.data?.error || "").toLowerCase();
            if (errMsg.includes("no record") || errMsg.includes("not found")) return null;
            throw new Error(res.data?.error || "Data status check failed.");
          }
          const { fi_request_status, link_ref_numbers, link_ref_number } = res.data || {};
          const refs: string[] = link_ref_numbers?.length
            ? link_ref_numbers
            : link_ref_number ? [link_ref_number] : [];
          console.info(`[NiyoginAAFlow] Step 3 poll: fiRequestStatus=${fi_request_status}, refs=${refs.length}`);
          if (fi_request_status === "READY" && refs.length > 0) return refs;
          return null;
        }, MAX_DATA_POLLS, POLL_INTERVAL_MS, cancelPollRef);
        markStep(3, "done");

        // ── Step 4: Poll BSA session status for ALL linkRefNumbers (sequential) ─
        // Sequential (not parallel) to avoid rate-limiting from Niyogin.
        await sleep(STEP_DELAY_MS);
        activateStep(4);
        for (const ref of linkRefNumbers) {
          await pollUntil(async () => {
            const res = await axios.get(
              `${API_BASE_URL}/api/niyogin/aa/bsa/status/${sessionId}/${ref}/`
            );
            if (res.data?.success === false) throw new Error(res.data?.error || `BSA status check failed for ${ref}.`);
            const { bsa_status } = res.data || {};
            console.info(`[NiyoginAAFlow] Step 4 poll: bsaStatus=${bsa_status} for ref=${ref}`);
            if (bsa_status === "COMPLETED" || bsa_status === "READY" || bsa_status === "READY_WITH_NO_TRANSACTIONS") return true as const;
            return null;
          }, MAX_BSA_POLLS, POLL_INTERVAL_MS, cancelPollRef);
        }
        markStep(4, "done");

        // ── Step 5: Fetch BSA report ────────────────────────────────────────────
        await sleep(STEP_DELAY_MS);
        activateStep(5);
        const reportRes = await axios.post(`${API_BASE_URL}/api/niyogin/aa/bsa/report/`, {
          session_id:       sessionId,
          link_ref_numbers: linkRefNumbers,
        });
        if (reportRes.data?.success === false) {
          const firstErr = reportRes.data?.errors?.[0]?.error;
          throw new Error(firstErr || reportRes.data?.error || "Failed to fetch BSA report.");
        }
        const savedCount = (reportRes.data?.reports ?? []).length;
        console.info(`[NiyoginAAFlow] BSA reports saved: ${savedCount}/${linkRefNumbers.length} accounts`);
        markStep(5, "done");

        activateStep(6);
        markStep(6, "done");

        sessionStorage.removeItem("niyogin_aa_consent_handle_id");
        sessionStorage.removeItem("niyogin_aa_customer_id");
        sessionStorage.removeItem("niyogin_aa_statement_type");

        setPhase("done");
      } catch (err: any) {
        if (cancelPollRef.current) return;
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          (err instanceof Error ? err.message : String(err));
        console.error("[NiyoginAAFlow] Pipeline error:", msg);
        setError(msg);
        setPhase("error");
        setSteps((prev) =>
          prev.map((s) => (s.status === "active" ? { ...s, status: "error" } : s))
        );
      }
    },
    [consentHandleId, customerId, activateStep, markStep]
  );

  // ── Background consent polling ────────────────────────────────────────────────
  // Polls GET /consent/status/:handleId/:custId every 5 seconds.
  // Updates bgPollCount so the UI can show "Checking… attempt N / MAX".
  // Auto-transitions to pipeline as soon as consentStatus === "ACCEPTED".
  const startBackgroundConsentPoll = useCallback(
    async (handleId: string, custId: string) => {
      // Always reset the cancel flag before starting a new poll loop
      cancelPollRef.current = false;
      setBgPollCount(0);
      for (let i = 0; i < MAX_BG_CONSENT_POLLS; i++) {
        if (cancelPollRef.current) return;
        await sleep(BG_POLL_INTERVAL_MS);          // wait 5 s before each check
        if (cancelPollRef.current) return;
        setBgPollCount(i + 1);                     // update visible attempt counter
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/niyogin/aa/consent/status/${handleId}/${custId}/`
          );
          const { consent_status, consent_id } = res.data || {};
          console.info(
            `[NiyoginAAFlow] BG poll #${i + 1}: consentStatus=${consent_status}`
          );
          if (consent_status === "ACCEPTED" && consent_id) {
            console.info("[NiyoginAAFlow] Background poll: consent ACCEPTED — starting pipeline");
            startPipeline(handleId, custId);
            return;
          }
        } catch {
          // Ignore individual poll errors — keep retrying
        }
      }
      console.warn("[NiyoginAAFlow] Background consent poll timed out after", MAX_BG_CONSENT_POLLS, "attempts");
    },
    [startPipeline]
  );

  // ── Phase 1: Initiate consent ─────────────────────────────────────────────────
  const initiateConsent = useCallback(async () => {
    if (initiateCalledRef.current) return;
    initiateCalledRef.current = true;
    setPhase("initiating");
    setError(null);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/niyogin/aa/consent/initiate/`, {
        statement_type: statementType,
        statement_mode: "PERIODIC",
      });
      if (res.data?.success === false) throw new Error(res.data?.error || "AA consent initiation failed.");

      const { consent_handle_id, redirect_url, customer_id } = res.data || {};

      if (consent_handle_id) {
        sessionStorage.setItem("niyogin_aa_consent_handle_id", consent_handle_id);
        setConsentHandleId(consent_handle_id);
      }
      if (customer_id) {
        sessionStorage.setItem("niyogin_aa_customer_id", customer_id);
        setCustomerId(customer_id);
      }
      // Persist statement_type so pipeline resume can read it
      sessionStorage.setItem("niyogin_aa_statement_type", statementType);

      if (redirect_url) {
        setAaUrl(redirect_url);
        window.open(redirect_url, "_blank", "noopener,noreferrer");
        setPhase("awaiting_user");
        startBackgroundConsentPoll(consent_handle_id, customer_id);
      } else {
        startPipeline(consent_handle_id, customer_id);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginAAFlow] Consent initiation error:", msg);
      setError(msg);
      setPhase("error");
      initiateCalledRef.current = false;
    }
  }, [statementType, startBackgroundConsentPoll, startPipeline]);

  // ── Auto-resume on mount ──────────────────────────────────────────────────────
  // Uses a ref so the effect only fires once on mount regardless of dependency changes.
  const resumeCalledRef = useRef(false);

  useEffect(() => {
    if (isBuilderMode) return;
    if (resumeCalledRef.current) return;
    resumeCalledRef.current = true;

    const resume = async () => {
      const storedHandleId   = sessionStorage.getItem("niyogin_aa_consent_handle_id") || "";
      const storedCustomerId = sessionStorage.getItem("niyogin_aa_customer_id") || "";

      // ── Try server session first ──────────────────────────────────────────────
      try {
        const res = await axios.get(`${API_BASE_URL}/api/niyogin/aa/session/latest/`);
        if (res.data?.success) {
          if (res.data.status === "REPORT_SAVED") {
            onDone?.();
            return;
          }

          // Restore statement_type from server if not in sessionStorage
          if (res.data.statement_type) {
            const storedType = sessionStorage.getItem("niyogin_aa_statement_type");
            if (!storedType) {
              sessionStorage.setItem("niyogin_aa_statement_type", res.data.statement_type);
            }
            setStatementType((res.data.statement_type as StatementType) || "SALARY");
          }

          let resolvedHandleId   = storedHandleId;
          let resolvedCustomerId = storedCustomerId;

          if (!resolvedHandleId && res.data.consent_handle_id) {
            resolvedHandleId = res.data.consent_handle_id;
            sessionStorage.setItem("niyogin_aa_consent_handle_id", resolvedHandleId);
            setConsentHandleId(resolvedHandleId);
          }
          if (!resolvedCustomerId && res.data.customer_id) {
            resolvedCustomerId = res.data.customer_id;
            sessionStorage.setItem("niyogin_aa_customer_id", resolvedCustomerId);
            setCustomerId(resolvedCustomerId);
          }

          if (resolvedHandleId && resolvedCustomerId) {
            if (res.data.status === "INITIATED") {
              setPhase("awaiting_user");
              startBackgroundConsentPoll(resolvedHandleId, resolvedCustomerId);
            } else {
              startPipeline(resolvedHandleId, resolvedCustomerId);
            }
            return;
          }
        }
        // success=false or no IDs → fall through to selecting
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) {
          // Backend has no active session — clear any stale sessionStorage IDs
          // so we don't accidentally resume a dead consent flow.
          sessionStorage.removeItem("niyogin_aa_consent_handle_id");
          sessionStorage.removeItem("niyogin_aa_customer_id");
          sessionStorage.removeItem("niyogin_aa_statement_type");
          console.info("[NiyoginAAFlow] No session (404) — cleared sessionStorage, showing selecting screen");
          // Stay on default "selecting" phase
          return;
        }
        // Other network errors (500, timeout, etc.) — if we have sessionStorage IDs,
        // try to resume the bg poll (server might be temporarily unavailable).
        if (storedHandleId && storedCustomerId) {
          console.info("[NiyoginAAFlow] Network error but sessionStorage has IDs — resuming bg poll");
          setPhase("awaiting_user");
          startBackgroundConsentPoll(storedHandleId, storedCustomerId);
          return;
        }
        console.warn("[NiyoginAAFlow] session/latest/ error:", status, err?.message);
      }

      // No server session and no sessionStorage IDs → stay on selecting screen (default phase)
    };

    resume();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuilderMode]);

  // ── Retry ─────────────────────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    pipelineStartedRef.current = false;
    cancelPollRef.current = false;
    setError(null);
    setSteps(PIPELINE_STEPS.map((label, i) => ({ label, status: i === 0 ? "active" : "pending" })));
    const handleId = sessionStorage.getItem("niyogin_aa_consent_handle_id") || consentHandleId;
    const custId   = sessionStorage.getItem("niyogin_aa_customer_id") || customerId;
    if (!handleId || !custId) {
      initiateCalledRef.current = false;
      setPhase("selecting");
      return;
    }
    startPipeline(handleId, custId);
  }, [consentHandleId, customerId, startPipeline]);

  // ── Step icon ─────────────────────────────────────────────────────────────────
  const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
    if (status === "done") return <span className="text-intellend-green font-bold text-lg">✓</span>;
    if (status === "error") return <span className="text-red-500 font-bold text-lg">✗</span>;
    if (status === "active") {
      return (
        <svg className="animate-spin h-5 w-5 text-intellend-green" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      );
    }
    return <span className="h-5 w-5 rounded-full border-2 border-gray-300 inline-block" />;
  };

  // ── Builder/preview mode ──────────────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏦</div>
        <p className="font-semibold text-gray-500">Niyogin: AA Consent + BSA Pipeline</p>
        <p className="text-xs mt-1">
          User selects SALARY or SME type, then initiates AA consent, opens the consent page in a
          new tab, and automatically polls consent status → FI request → data status → BSA analysis
          → BSA report (routed to correct endpoint based on type).
        </p>
      </div>
    );
  }

  // ── Shared step list renderer ─────────────────────────────────────────────────
  const StepList = () => (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <div
          key={i}
          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
            step.status === "active"
              ? "bg-blue-50 border border-blue-200"
              : step.status === "done"
              ? "bg-green-50 border border-green-200"
              : step.status === "error"
              ? "bg-red-50 border border-red-200"
              : "bg-gray-50 border border-gray-100"
          }`}
        >
          <div className="flex-shrink-0 w-6 flex items-center justify-center">
            <StepIcon status={step.status} />
          </div>
          <span
            className={`text-sm font-medium ${
              step.status === "active"
                ? "text-blue-700"
                : step.status === "done"
                ? "text-green-700"
                : step.status === "error"
                ? "text-red-700"
                : "text-gray-400"
            }`}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );

  // ── Phase: selecting (SALARY / SME) ──────────────────────────────────────────
  if (phase === "selecting") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Bank Statement Analysis</h2>
              <p className="text-sm text-gray-500">
                Select the type of bank statement you want to analyse. This determines which
                report is generated for your loan application.
              </p>
            </div>

            {/* Type selection cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* SALARY card */}
              <button
                onClick={() => setStatementType("SALARY")}
                className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                  statementType === "SALARY"
                    ? "border-intellend-green bg-green-50 shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <span className="text-3xl">💼</span>
                <div className="text-center">
                  <p className={`font-semibold text-sm ${statementType === "SALARY" ? "text-intellend-green" : "text-gray-700"}`}>
                    Salary
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Salaried employee</p>
                </div>
                {statementType === "SALARY" && (
                  <span className="text-intellend-green text-lg font-bold">✓</span>
                )}
              </button>

              {/* SME card */}
              <button
                onClick={() => setStatementType("SME")}
                className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                  statementType === "SME"
                    ? "border-intellend-green bg-green-50 shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <span className="text-3xl">🏪</span>
                <div className="text-center">
                  <p className={`font-semibold text-sm ${statementType === "SME" ? "text-intellend-green" : "text-gray-700"}`}>
                    SME / Business
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Self-employed / business</p>
                </div>
                {statementType === "SME" && (
                  <span className="text-intellend-green text-lg font-bold">✓</span>
                )}
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-800">
                {statementType === "SALARY" ? "💼 Salary Analysis" : "🏪 SME / Business Analysis"}
              </p>
              <p className="text-sm text-blue-700">
                {statementType === "SALARY"
                  ? "We'll analyse your salary credits, recurring debits, and overall cash flow to assess your repayment capacity."
                  : "We'll analyse your business transactions, revenue patterns, and cash flow to assess your business loan eligibility."}
              </p>
            </div>

            <button
              onClick={() => setPhase("idle")}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Continue with {statementType === "SALARY" ? "Salary" : "SME / Business"} →
            </button>
            <p className="text-xs text-center text-gray-400">
              🔒 Secured by RBI-regulated Account Aggregator framework
            </p>
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: idle ───────────────────────────────────────────────────────────────
  if (phase === "idle") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Bank Statement Analysis</h2>
              <p className="text-sm text-gray-500">
                We need to analyse your{" "}
                <span className="font-semibold text-intellend-green">
                  {statementType === "SALARY" ? "salary" : "business"}
                </span>{" "}
                bank statements via Account Aggregator (AA). This is a secure, RBI-regulated
                process — your bank data is shared only with your consent.
              </p>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
              <span className="text-lg">{statementType === "SALARY" ? "💼" : "🏪"}</span>
              <span className="text-sm font-medium text-gray-700">
                {statementType === "SALARY" ? "Salary Statement" : "SME / Business Statement"}
              </span>
              <button
                onClick={() => setPhase("selecting")}
                className="ml-auto text-xs text-intellend-green underline hover:text-intellend-green/80"
              >
                Change
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-800">What happens next:</p>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>An AA consent page opens in a new tab</li>
                <li>You approve sharing your bank statement data</li>
                <li>We automatically analyse your statements</li>
                <li>Your loan application is processed faster</li>
              </ol>
            </div>
            <button
              onClick={initiateConsent}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Start Bank Account Aggregation →
            </button>
            <p className="text-xs text-center text-gray-400">
              🔒 Secured by RBI-regulated Account Aggregator framework
            </p>
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: initiating ─────────────────────────────────────────────────────────
  if (phase === "initiating") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Setting Up Consent…</h2>
            <p className="text-gray-500 text-sm">Preparing your Account Aggregator consent request…</p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: awaiting_user ──────────────────────────────────────────────────────
  if (phase === "awaiting_user") {
    const pollsRemaining = MAX_BG_CONSENT_POLLS - bgPollCount;
    const secondsRemaining = pollsRemaining * (BG_POLL_INTERVAL_MS / 1000);
    const minutesLeft = Math.floor(secondsRemaining / 60);
    const secsLeft = secondsRemaining % 60;
    const timeLabel = minutesLeft > 0
      ? `${minutesLeft}m ${secsLeft}s`
      : `${secsLeft}s`;

    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Approve Bank Data Consent</h2>
              <p className="text-sm text-gray-500">
                The Account Aggregator consent page has opened in a new tab. Approve the consent
                there — we'll automatically detect it and continue.
              </p>
            </div>

            <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside bg-blue-50 border border-blue-100 rounded-lg p-4">
              <li>In the new tab, log in to your bank's AA portal</li>
              <li>Review and approve the data sharing consent</li>
              <li>We'll automatically detect your approval and continue</li>
            </ol>

            {/* Live polling status — updates every 5 seconds */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-500 justify-center">
                <svg className="animate-spin h-3 w-3 text-intellend-green flex-shrink-0" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                {bgPollCount === 0
                  ? "Waiting to check consent status…"
                  : `Checking consent status every 5s — attempt ${bgPollCount} / ${MAX_BG_CONSENT_POLLS}`}
              </div>
              {bgPollCount > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-intellend-green h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((bgPollCount / MAX_BG_CONSENT_POLLS) * 100, 100)}%` }}
                  />
                </div>
              )}
              {bgPollCount > 0 && (
                <p className="text-xs text-center text-gray-400">
                  Auto-expires in ~{timeLabel} if consent is not detected
                </p>
              )}
            </div>

            {aaUrl && (
              <p className="text-xs text-center text-gray-400">
                Tab closed?{" "}
                <a href={aaUrl} target="_blank" rel="noopener noreferrer" className="text-intellend-green underline">
                  Reopen AA Consent Page
                </a>
              </p>
            )}

            <button
              onClick={() => {
                pipelineStartedRef.current = false;
                startPipeline(consentHandleId, customerId);
              }}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              ✅ I've Approved Consent — Continue
            </button>
            <button
              onClick={() => {
                initiateCalledRef.current = false;
                setBgPollCount(0);
                setError(null);
                setAaUrl(null);
                initiateConsent();
              }}
              className="w-full py-2 px-6 text-sm text-gray-500 underline hover:text-gray-700 transition-colors"
            >
              Re-initiate Consent
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: pipeline ───────────────────────────────────────────────────────────
  if (phase === "pipeline") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">📊</div>
              <h2 className="text-xl font-bold text-gray-800">Analysing Bank Statements…</h2>
              <p className="text-sm text-gray-500">
                Please wait while we securely fetch and analyse your bank data.
              </p>
            </div>
            <StepList />
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: error ──────────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">⚠️</div>
              <h2 className="text-xl font-bold text-gray-800">Analysis Failed</h2>
            </div>
            <StepList />
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-1">
                <p className="text-sm font-semibold text-red-700">❌ Error</p>
                <p className="text-sm text-red-600">{error}</p>
                <p className="text-xs text-red-500">Please retry or contact support.</p>
              </div>
            )}
            <button
              onClick={handleRetry}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              Retry Analysis
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Phase: done ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="text-5xl">🎉</div>
            <h2 className="text-xl font-bold text-gray-800">Bank Analysis Complete!</h2>
          </div>
          <StepList />
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-sm font-semibold text-green-700">
              ✅ Bank statement analysis saved successfully!
            </p>
            <p className="text-xs text-green-600 mt-1">
              Your loan application is being processed.
            </p>
          </div>
          <button
            onClick={() => onDone?.()}
            className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
          >
            Continue →
          </button>
        </div>
      </main>
    </div>
  );
};

export default NiyoginAAFlow;
